import { CheckCircle2, LayoutGrid, Plus, RefreshCw, Search, XCircle } from 'lucide-react';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Domain, DomainStats } from '../../types';
import { domainService } from '../../services/domainService';
import forgeService from '../../services/forgeService';
import { useForgeApiEnabled } from '../../stores/forgeApiStore';
import { Button } from '../../components/Common/button';
import { PageLayout } from '../../components/Layout/PageLayout';
import { TableSkeleton } from '../../components/Common/TableSkeleton';

import { useTranslation } from 'react-i18next';
import { useIsOrganizationAdmin } from '../../stores/authStore';

const DomainsListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [forgeNames, setForgeNames] = useState<Record<string, string>>({});
  const [statsById, setStatsById] = useState<Record<string, DomainStats>>({});
  const [loading, setLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [isForgesLoading, setIsForgesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const showForge = useForgeApiEnabled() === true;
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [quickFilter, setQuickFilter] = useState<'all' | 'with_framings' | 'with_use_cases'>('all');
  const { t } = useTranslation();
  const isAdmin = useIsOrganizationAdmin();

  const truncateWords = (value: string, maxWords = 8) => {
    const words = value.trim().split(/\s+/);
    if (words.length <= maxWords) return value;
    return `${words.slice(0, maxWords).join(' ')}...`;
  };

  // Handle success message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, [location.state]);

  useEffect(() => {
    const loadDomains = async () => {
      try {
        setLoading(true);
        setError(null);

        const basicDomains = await domainService.getUserDomains();
        setDomains(basicDomains);
      } catch (err) {
        console.error('Failed to load domains', err);
        setError(t('domains.list.error'));
      } finally {
        setLoading(false);
      }
    };

    loadDomains();
  }, [t]);

  // Load per-domain stats in the background — table renders immediately and
  // counts cells show a spinner until each domain's stats arrive.
  useEffect(() => {
    if (domains.length === 0) {
      setIsStatsLoading(false);
      return;
    }
    let ignore = false;
    setIsStatsLoading(true);
    Promise.all(
      domains.map(async (domain) => {
        try {
          const stats = await domainService.getDomainStats(domain.id);
          return [domain.id, stats] as const;
        } catch (err) {
          console.error(`Failed to load stats for domain ${domain.id}`, err);
          return null;
        }
      })
    )
      .then((results) => {
        if (ignore) return;
        const map: Record<string, DomainStats> = {};
        for (const entry of results) {
          if (entry) map[entry[0]] = entry[1];
        }
        setStatsById(map);
      })
      .finally(() => {
        if (!ignore) setIsStatsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [domains]);

  // Load Forge names in the background — used only to decorate the
  // "Forge" column, so the table doesn't need to wait on it.
  useEffect(() => {
    if (!showForge) return;
    let ignore = false;
    setIsForgesLoading(true);
    forgeService
      .listForges()
      .then(async (forges) => {
        if (ignore) return;
        const forgeMap: Record<string, string> = {};
        forges.forEach((forge) => {
          forgeMap[forge.forge_id] = forge.display_name || forge.name || forge.forge_id;
        });

        // Resolve any forge IDs referenced by domains but missing from the bulk list.
        const unresolvedForgeIds = Array.from(
          new Set(
            domains
              .map((d) => d.forge_id)
              .filter((id): id is string => Boolean(id) && !forgeMap[id as string])
          )
        );
        if (unresolvedForgeIds.length > 0) {
          const resolvedForges = await Promise.all(
            unresolvedForgeIds.map((forgeId) =>
              forgeService.getForge(forgeId).catch(() => null)
            )
          );
          if (ignore) return;
          resolvedForges.forEach((forge, idx) => {
            if (!forge) return;
            const fallbackId = unresolvedForgeIds[idx];
            forgeMap[fallbackId] = forge.display_name || forge.name || fallbackId;
          });
        }
        if (!ignore) setForgeNames(forgeMap);
      })
      .catch((err) => {
        console.warn('Unable to load Forge names:', err);
      })
      .finally(() => {
        if (!ignore) setIsForgesLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [domains, showForge]);

  const filteredDomains = useMemo(() => {
    const q = query.toLowerCase().trim();
    let list = domains;
    if (q) {
      list = list.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.description || '').toLowerCase().includes(q)
      );
    }
    if (quickFilter === 'with_framings') {
      list = list.filter((d) => (statsById[d.id]?.total_cadrages ?? 0) > 0);
    } else if (quickFilter === 'with_use_cases') {
      list = list.filter((d) => (statsById[d.id]?.total_use_cases ?? 0) > 0);
    }
    return list;
  }, [domains, query, quickFilter, statsById]);

  const handleCreateDomain = () => {
    navigate('/domains/new');
  };

  const handleDomainClick = (domainId: string) => {
    navigate(`/domains/${domainId}`);
  };

  return (
    <PageLayout
      icon={<LayoutGrid className="h-6 w-6" />}
      title={t('domains.list.title')}
      subtitle={t('domains.list.subtitle')}
    >
      {/* Success Message */}
      {successMessage && (
        <div className="bg-accent/10 border border-accent rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-accent-foreground" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-foreground">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search + quick filters + New domain card */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-muted-foreground" />
            </div>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('domains.list.searchPlaceholder', 'Search domains...')}
              className="w-full pl-10 pr-3 py-2 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {(
              [
                {
                  value: 'all' as const,
                  label: t('global.all'),
                  count: loading ? null : domains.length,
                },
                {
                  value: 'with_framings' as const,
                  label: t('domains.list.quickFilter.withFramings', 'With solutions'),
                  count:
                    loading || isStatsLoading
                      ? null
                      : domains.filter((d) => (statsById[d.id]?.total_cadrages ?? 0) > 0).length,
                },
                {
                  value: 'with_use_cases' as const,
                  label: t('domains.list.quickFilter.withUseCases', 'With use cases'),
                  count:
                    loading || isStatsLoading
                      ? null
                      : domains.filter((d) => (statsById[d.id]?.total_use_cases ?? 0) > 0).length,
                },
              ] as const
            ).map(({ value, label, count }) => (
              <button
                key={value}
                type="button"
                onClick={() => setQuickFilter(value)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  quickFilter === value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {label}
                <span className="ml-1 opacity-80">({count === null ? '—' : count})</span>
              </button>
            ))}
            {isAdmin && (
              <Button className="flex items-center gap-2" onClick={handleCreateDomain}>
                <Plus className="h-4 w-4" />
                {t('domains.list.new')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-destructive/10 border border-destructive rounded-lg p-6 text-center text-destructive">
          <p className="mb-4">{error}</p>
          <Button onClick={() => window.location.reload()} variant="outline" className="text-foreground">
            {t('global.retry')}
          </Button>
        </div>
      )}

      {/* Table */}
      {!error && (
        <div className="rounded-xl border border-border bg-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            {loading ? (
              <TableSkeleton rows={6} cols={6} />
            ) : filteredDomains.length === 0 ? (
              <div className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Plus className="h-8 w-8 text-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {query ? t('global.noResults', 'No results') : t('domains.list.emptyTitle')}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {query ? t('global.tryDifferentSearch', 'Try a different search.') : t('domains.list.emptyDescription')}
                  </p>
                  {!query && isAdmin && (
                    <Button onClick={handleCreateDomain} className="flex items-center gap-2 mx-auto">
                      <Plus className="h-5 w-5" />
                      {t('domains.list.emptyCreate')}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <table className="min-w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('domains.list.tableName', 'Name')}
                    </th>
                    {showForge && (
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('global.forge', 'Forge')}
                    </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('domains.card.members', 'Members')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('domains.card.cadrages', 'Solutions')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('domains.card.useCases', 'Use cases')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('global.lastModified', 'Last modified')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {filteredDomains.map((domain) => (
                    <tr
                      key={domain.id}
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => handleDomainClick(domain.id)}
                    >
                      <td className="px-4 py-3 max-w-xs">
                        <span className="text-sm font-semibold text-foreground" title={domain.name}>
                          {truncateWords(domain.name, 8)}
                        </span>
                      </td>
                      {showForge && (
                      <td className="px-4 py-3">
                        {domain.forge_id ? (
                          isForgesLoading ? (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              <RefreshCw className="h-3 w-3 animate-spin" />
                              {t('global.loading', 'Loading...')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300">
                              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                              <span className="font-medium">{t('global.yes', 'Yes')}</span>
                              <span className="text-emerald-700/80 dark:text-emerald-300/80">
                                - {forgeNames[domain.forge_id] || t('global.notAvailable', 'N/A')}
                              </span>
                            </span>
                          )
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                            <XCircle className="h-3.5 w-3.5 shrink-0" />
                            <span className="font-medium">{t('global.no', 'No')}</span>
                          </span>
                        )}
                      </td>
                      )}
                      <td className="px-4 py-3 text-sm text-foreground/80">
                        {statsById[domain.id] ? (
                          statsById[domain.id].total_members
                        ) : isStatsLoading ? (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <RefreshCw className="h-3 w-3 animate-spin" />
                            {t('global.loading', 'Loading...')}
                          </span>
                        ) : (
                          0
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">
                        {statsById[domain.id] ? (
                          statsById[domain.id].total_cadrages
                        ) : isStatsLoading ? (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <RefreshCw className="h-3 w-3 animate-spin" />
                            {t('global.loading', 'Loading...')}
                          </span>
                        ) : (
                          0
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">
                        {statsById[domain.id] ? (
                          statsById[domain.id].total_use_cases
                        ) : isStatsLoading ? (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <RefreshCw className="h-3 w-3 animate-spin" />
                            {t('global.loading', 'Loading...')}
                          </span>
                        ) : (
                          0
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80 whitespace-nowrap">
                        {new Date(domain.updated_at || domain.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default DomainsListPage;
